
import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/e3f086a2-0367-488a-9c8c-5ddda91903cf.png" 
              alt="SHERLOK LTDA Logo" 
              className="h-10 w-10 object-contain"
            />
            <h1 className="text-2xl font-bold text-gray-900">SHERLOK LTDA</h1>
          </div>
          <nav className="flex items-center gap-10">
            <button
              onClick={() => scrollToSection('about')}
              className="text-base text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('products')}
              className="text-base text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              Products
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className="text-base text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              Features
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-base text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              Contact
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-7xl font-serif font-bold text-gray-900 leading-tight mb-6">
            Modern iOS Applications
            <br />
            for Enhanced Security & Performance
          </h2>
          <p className="text-2xl text-gray-600 max-w-3xl mx-auto">
            Expert team crafting premium VPN and Cleaner solutions for iOS devices
          </p>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-10 items-center">
            <div className="w-2/5">
              <div className="bg-gradient-to-br from-purple-600 to-gray-800 rounded-3xl p-14">
                <div className="flex gap-3 mb-6">
                  <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm whitespace-nowrap">
                    iOS Experts
                  </span>
                  <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm whitespace-nowrap">
                    Security Focused
                  </span>
                  <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm whitespace-nowrap">
                    User-Centric
                  </span>
                </div>
                <p className="text-lg text-white leading-relaxed">
                  SHERLOK LTDA is a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices. We combine cutting-edge technology with intuitive design to deliver solutions that protect your privacy while optimizing device performance. Our commitment to excellence drives us to continuously innovate and provide the best possible experience for iOS users worldwide.
                </p>
              </div>
            </div>
            <div className="w-3/5">
              <img
                src="https://public.readdy.ai/ai/img_res/06eab0cd2611d4bb76194ea66673a443.jpg"
                alt="iOS Security Applications"
                className="w-full h-auto rounded-2xl shadow-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-6xl font-serif font-bold text-gray-900 mb-10">Our Products</h3>
          <div className="grid grid-cols-2 gap-10">
            {/* VPN Card */}
            <div className="bg-slate-800 rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-indigo-500/30 cursor-pointer">
              <div className="h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20digital%20VPN%20encryption%20visualization%20with%20flowing%20cyan%20and%20purple%20data%20streams%2C%20network%20security%20concept%20with%20glowing%20encrypted%20tunnels%2C%20modern%20tech%20illustration%20with%20gradient%20mesh%20effects%2C%20secure%20connection%20pathways%20with%20particle%20effects%2C%20dark%20background%20with%20luminous%20cybersecurity%20elements%20and%20geometric%20patterns&width=600&height=700&seq=vpn-product-card&orientation=portrait"
                  alt="VPN Product"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <h4 className="text-4xl font-bold text-white mb-4">VPN</h4>
                <ul className="space-y-3 text-gray-300 text-base">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span>Military-grade encryption for complete privacy</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span>Lightning-fast connection speeds worldwide</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span>No-logs policy ensuring your data stays private</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Cleaner Card */}
            <div className="bg-emerald-50 rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer">
              <div className="h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Minimalist%20iPhone%20cleaning%20illustration%20with%20emerald%20green%20particles%20and%20sparkles%2C%20mobile%20device%20optimization%20concept%20with%20clean%20simple%20background%2C%20modern%20tech%20graphic%20showing%20phone%20with%20cleaning%20effects%2C%20soft%20mint%20and%20emerald%20color%20palette%2C%20professional%20app%20interface%20visualization%20with%20floating%20cleaning%20elements%20and%20smooth%20gradients&width=600&height=700&seq=cleaner-product-card&orientation=portrait"
                  alt="Cleaner Product"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <h4 className="text-4xl font-bold text-emerald-900 mb-4">Cleaner</h4>
                <ul className="space-y-3 text-gray-700 text-base">
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-1">•</span>
                    <span>Deep cleaning removes junk files and cache</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-1">•</span>
                    <span>Optimize storage and boost device performance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-1">•</span>
                    <span>Smart photo management and duplicate removal</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-5xl font-serif font-bold text-gray-900 text-center mb-16">Key Features</h3>
          <div className="grid grid-cols-3 gap-8">
            {/* Security First */}
            <div className="bg-white rounded-xl p-10 border border-gray-200 transition-all duration-300 hover:shadow-lg">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-white text-4xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Security First</h4>
              <p className="text-base text-gray-600 leading-7">
                Advanced encryption and security protocols protect your data and privacy at all times, ensuring complete peace of mind while using our applications.
              </p>
            </div>

            {/* Performance Optimized */}
            <div className="bg-white rounded-xl p-10 border border-gray-200 transition-all duration-300 hover:shadow-lg">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center mb-6">
                <i className="ri-flashlight-line text-white text-4xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Performance Optimized</h4>
              <p className="text-base text-gray-600 leading-7">
                Our applications are engineered for maximum efficiency, delivering fast performance without draining your battery or consuming excessive resources.
              </p>
            </div>

            {/* Privacy Protected */}
            <div className="bg-white rounded-xl p-10 border border-gray-200 transition-all duration-300 hover:shadow-lg">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center mb-6">
                <i className="ri-lock-line text-white text-4xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Privacy Protected</h4>
              <p className="text-base text-gray-600 leading-7">
                We never collect, store, or share your personal information. Your privacy is our top priority, and we maintain strict no-logs policies across all products.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-16">
            <div className="w-1/2 pl-14">
              <h3 className="text-6xl font-serif font-bold text-white leading-tight">
                Get in Touch
              </h3>
            </div>
            <div className="w-1/2">
              <div className="grid grid-cols-2 gap-x-8 gap-y-10">
                {/* Address */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-map-pin-line text-purple-400 text-xl"></i>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Address</p>
                  </div>
                  <p className="text-lg text-white">Av. SAO JOAQUIM 1014 ANEXO 1</p>
                </div>

                {/* City */}
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">City</p>
                  <p className="text-lg text-white">BOA VISTA</p>
                </div>

                {/* Zip */}
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Zip Code</p>
                  <p className="text-lg text-white">69314-342</p>
                </div>

                {/* Country */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-flag-line text-purple-400 text-xl"></i>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Country</p>
                  </div>
                  <p className="text-lg text-white">Brazil</p>
                </div>

                {/* Email */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-mail-line text-purple-400 text-xl"></i>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Email</p>
                  </div>
                  <p className="text-lg text-white">contact@sherlokltda.com</p>
                </div>

                {/* Phone */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-phone-line text-purple-400 text-xl"></i>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Phone</p>
                  </div>
                  <p className="text-lg text-white">+1 8127690854</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 border-t border-gray-700 py-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-400">
            © 2025 SHERLOK LTDA. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300 transition-colors">Website Builder</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
